import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFeaturedProducts, getMostViewedProducts } from '@3dera/core/services/products';
import { refreshStorageURL } from '@3dera/core/services/storage';
import { useLanguage } from '../../context/LanguageContext';
import PriceTag from '../PriceTag/PriceTag';
import styles from './FeaturedModels.module.css';

const PLACEHOLDER_SVG = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2U1ZTdlYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj7EsMWfbHNlbCBCdWx1bmFtYWRpPC90ZXh0Pjwvc3ZnPg==';

const FeaturedImage = ({ product, index }) => {
  const [src, setSrc] = useState(product.thumbnails?.[0] || product.images?.[0] || PLACEHOLDER_SVG);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const img = product.thumbnails?.[0] || product.images?.[0];
    if (!img) { setSrc(PLACEHOLDER_SVG); setLoaded(true); return; }
    if (img.includes('firebasestorage.googleapis.com')) {
      refreshStorageURL(img).then(r => setSrc(r || img)).catch(() => setSrc(img));
    } else {
      setSrc(img);
    }
  }, [product.id]);

  return (
    <>
      {!loaded && <div className={styles.imageShimmer} />}
      <img
        src={src}
        alt={product.name}
        className={`${styles.modelImage} ${loaded ? styles.imageVisible : styles.imageHidden}`}
        loading={index < 3 ? 'eager' : 'lazy'}
        fetchPriority={index < 3 ? 'high' : 'auto'}
        onLoad={() => setLoaded(true)}
        onError={() => { setSrc(PLACEHOLDER_SVG); setLoaded(true); }}
      />
    </>
  );
};

const FeaturedModels = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFeatured, setIsFeatured] = useState(true);

  useEffect(() => {
    const load = async () => {
      // Önce öne çıkan ürünleri dene
      const featuredResult = await getFeaturedProducts(6);
      if (featuredResult.success && featuredResult.products.length > 0) {
        setProducts(featuredResult.products);
        setIsFeatured(true);
      } else {
        // Öne çıkan yoksa en çok görüntülenenleri göster
        const mostViewed = await getMostViewedProducts(6);
        if (mostViewed.success && mostViewed.products.length > 0) {
          setProducts(mostViewed.products);
          setIsFeatured(false);
        }
      }
      setLoading(false);
    };
    load();
  }, []);

  const handleMarketplaceClick = () => {
    navigate('/marketplace');
  };

  // Hiç ürün yoksa bölümü gösterme
  if (!loading && products.length === 0) {
    return null;
  }

  return (
    <section className={styles.featuredSection}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>{isFeatured ? t('home.featuredTitle') : t('home.mostViewedTitle')}</h2>
          <button onClick={handleMarketplaceClick} className={styles.marketplaceLink}>
            {t('home.featuredViewAll')}
            <svg className={styles.arrowIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className={styles.modelsGrid}>
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={styles.modelCard}>
                <div className={styles.imageContainer}>
                  <div className={styles.imageShimmer} />
                </div>
                <div className={styles.modelInfo}>
                  <div className={styles.skeletonLine} style={{ width: '70%', height: 18 }} />
                  <div className={styles.skeletonLine} style={{ width: '50%', height: 14, marginTop: 8 }} />
                  <div className={styles.skeletonLine} style={{ width: '40%', height: 14, marginTop: 8 }} />
                </div>
              </div>
            ))
          ) : (
            products.map((product, index) => (
              <div
                key={product.id}
                className={styles.modelCard}
                onClick={() => navigate(`/product/${product.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className={styles.imageContainer}>
                  <FeaturedImage product={product} index={index} />
                </div>
                <div className={styles.modelInfo}>
                  <h3 className={styles.modelTitle}>{product.name}</h3>
                  <PriceTag product={product} />
                  {product.rating > 0 && (
                    <div className={styles.stats}>
                      <span className={styles.stat}>
                        <svg className={styles.statIcon} viewBox="0 0 20 20" fill="currentColor">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        {product.rating.toFixed(1)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedModels;
